/**
 * Account details › related tab copy: sign-in log, bound clubs, submitted applications,
 * generated orders.
 *
 * Kept as its own module and merged by `locale-resources.ts` instead of living inside the
 * thousand-line `locales/en-US.ts`: those files are touched by all three languages and are
 * a hotspot where a new key group easily collides with someone else's edit.
 *
 * Field names mirror the backend contracts: iam `LoginLogRow`, member `ClubAdminGrantRow`,
 * `ApplicationRecordResp`, and billing `BillingOrderRow`.
 */
export default {
  'acct.portal.loginLog.entryCount': '{name}: {count} visits',
  // ── Sign-in log (iam API-10-013) ──
  // `endName` is where the sign-in came from, `entry` is which entrance it led into —
  // the user side has only one end, so treating spaces as ends would double-count.
  'acct.portal.loginLog.title': 'Sign-in log ({count})',
  'acct.portal.loginLog.col.at': 'Time',
  'acct.portal.loginLog.col.end': 'Signed in from',
  'acct.portal.loginLog.col.ip': 'IP',
  'acct.portal.loginLog.col.device': 'Device',
  'acct.portal.loginLog.col.result': 'Result',
  'acct.portal.loginLog.ok': 'Succeeded',
  'acct.portal.loginLog.fail': 'Failed',
  'acct.portal.loginLog.enter': 'Entered: {space}',
  'acct.portal.loginLog.entry.site': 'website entrance',
  'acct.portal.loginLog.entry.member': 'member entrance',
  'acct.portal.loginLog.entry.club': 'club entrance',
  'acct.portal.loginLog.empty': 'No sign-in records yet',

  // ── Bound clubs (member clubAdminGrant): grants are about clubs, not about people ──
  'acct.portal.related.club.title': 'Bound clubs ({count})',
  'acct.portal.related.club.name': 'Club',
  'acct.portal.related.club.status': 'Status',
  'acct.portal.related.club.validity': 'Valid until',
  'acct.portal.related.club.granted': 'Granted',
  'acct.portal.related.club.noExpiry': 'No expiry date',
  'acct.portal.related.club.empty': 'This account is not authorised to manage any club',

  // ── Submitted applications (member API-11-007, initiator account) ──
  'acct.portal.related.application.title': 'Submitted applications ({count})',
  'acct.portal.related.application.no': 'No.',
  'acct.portal.related.application.type': 'Type',
  'acct.portal.related.application.onBehalfOf': 'On behalf of',
  'acct.portal.related.application.status': 'Status',
  'acct.portal.related.application.submittedAt': 'Submitted',
  'acct.portal.related.application.empty': 'No applications yet',

  // ── Generated orders (billing API-15-001): only orders this account placed itself ──
  'acct.portal.related.order.title': 'Generated orders ({count})',
  'acct.portal.related.order.no': 'Order no.',
  'acct.portal.related.order.type': 'Type',
  'acct.portal.related.order.amount': 'Amount',
  'acct.portal.related.order.beneficiary': 'Paid for',
  'acct.portal.related.order.status': 'Status',
  'acct.portal.related.order.empty': 'This account has not placed any order',

  // ── List bulk actions (prototype ADM.apBulkBar / apBulkGo) ──
  // "Send activation invites" targets the person bound as this account's self (本人);
  // rows without a self binding are reported as not done.
  'acct.portal.bulk.selected': '{count} selected',
  'acct.portal.bulk.disable': 'Disable in bulk',
  'acct.portal.bulk.enable': 'Re-enable in bulk',
  'acct.portal.bulk.invite': 'Send activation invites in bulk',
  'acct.portal.bulk.clear': 'Clear selection',
  'acct.portal.bulk.run': 'Run',
  'acct.portal.bulk.rowByRow':
    'Each row is checked on its own and reports its own result: rows that cannot be done say why and do not block the rest. The rules are exactly the same as acting row by row.',
  'acct.portal.bulk.inviteScope':
    'Invites go to the person bound as the self (本人) of each selected account; rows without such a binding are flagged.',
  'acct.portal.bulk.reasonLabel': 'Reason / note',
  'acct.portal.bulk.reasonOptional': 'Optional, written to the audit log',
  'acct.portal.bulk.reasonRequired':
    'Disabling in bulk needs a reason — it is what the user sees when blocked',
  'acct.portal.bulk.inviteNoSelf':
    'This account has no self binding, so there is nobody to invite',
  'acct.portal.bulk.inviteSkipped': 'Not sent',
  'acct.portal.bulk.noVersion': 'This row has no version; refresh and retry',
  'acct.portal.bulk.nothingSelected': 'Select some rows first',
  'acct.portal.bulk.requestFailed': 'The batch was not carried out',
  'acct.portal.bulk.resultTitle': 'Bulk action · results',
  'acct.portal.bulk.resultSummary':
    '{total} rows: {ok} done, {bad} not done. Rows already done do not need repeating.',
  'acct.portal.bulk.exportResult': 'Export results',
  'acct.portal.bulk.colAccount': 'Account',
  'acct.portal.bulk.colResult': 'Result',
  'acct.portal.bulk.colNote': 'Note',
  'acct.portal.bulk.ok': 'Done',
  'acct.portal.bulk.failed': 'Not done',
  'acct.req.col.subject': 'Subject',
  'acct.portal.detail.realNameUnbound': 'No self binding; not applicable',
  'acct.portal.detail.realNameVoucher': 'Verification ref.',
  'acct.portal.contact.maintain': 'Edit contact',
  'acct.portal.contact.create': 'Add contact',
  'acct.portal.contact.title': 'Account contact · {login}',
  'acct.portal.contact.phone': 'Phone',
  'acct.portal.contact.emailPlaceholder': 'Leave blank to use the login email',
  'acct.portal.contact.noSelfNotice': 'This account has no "self" binding — the holder is not a person of record here. No file is created for them: that would count towards headcount and quota. A contact group is just enough to reach whoever is behind this account.',
  'acct.portal.col.holderContact': 'Account contact {name}',
} as const;
